// Provider-agnostic chat interface. Built-in providers:
//   - 'anthropic'   → Anthropic Messages API
//   - 'openai'      → OpenAI Chat Completions
//   - 'gemini'      → Google Generative Language API (:generateContent)
//   - 'openline'    → Prakash Software's in-house Ollama-style endpoint
//   - 'custom'      → any OpenAI-compatible endpoint (self-hosted / gateway).
//                     Uses the OpenAI Chat Completions wire format against a
//                     caller-supplied base URL; no default host or model.
//
// anthropic/openai/gemini are "key-only": the host is canonical and the model
// list is discovered live from the provider (server/src/ai/models.ts), so the
// admin supplies nothing but an API key.
//
// Calls run server-side (the AI orchestrator) where keys are sealed.

export type Provider = 'anthropic' | 'openai' | 'gemini' | 'openline' | 'custom';

export interface ChatTurn {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface ChatOptions {
  provider: Provider;
  /** Defaults vary per provider. */
  model?: string;
  /** Optional system prompt — applied first if neither `messages[0]` is a system. */
  systemPrompt?: string;
  /** Max output tokens / completion length. Provider-specific clamp. */
  maxTokens?: number;
  /** 0…2; provider-specific clamp. */
  temperature?: number;
  /** Nucleus sampling, 0…1. Omitted when undefined so the provider default stands. */
  topP?: number;
  /** Up to 4 strings that cut generation short when produced. */
  stopSequences?: string[];
  /** Text deltas from a genuine provider stream. Omit for ordinary calls. */
  onDelta?: (text: string) => void;
  /** AbortController signal. */
  signal?: AbortSignal;
}

export interface ChatRequest {
  messages: ChatTurn[];
  opts: ChatOptions;
}

/** Token counts as the provider reported them. Absent when it reported none
 *  (some self-hosted gateways don't), which callers must treat as "unknown",
 *  never as zero — a cost model summing zeros would under-report. */
export interface ChatUsage {
  inputTokens: number;
  outputTokens: number;
}

export interface ChatResult {
  text: string;
  usage?: ChatUsage;
  /** Provider-native response object — kept for debugging, not for UI. */
  raw?: unknown;
}

export class ProviderError extends Error {
  constructor(
    public readonly provider: Provider,
    public readonly status: number,
    message: string,
    public readonly raw?: unknown,
  ) {
    super(`[${provider}] ${message}`);
    this.name = 'ProviderError';
  }
}

export const DEFAULT_MODELS: Record<Provider, string> = {
  anthropic: 'claude-sonnet-4-5-20250929',
  openai: 'gpt-4o-mini',
  gemini: 'gemini-2.0-flash',
  openline: 'qwen3:14b',
  // No sensible default for an arbitrary OpenAI-compatible endpoint — the site
  // must specify the model name its gateway expects.
  custom: '',
};

export const PROVIDER_LABEL: Record<Provider, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  gemini: 'Google Gemini',
  openline: 'Prakash Software (Ollama)',
  custom: 'OpenAI-compatible',
};

/**
 * Providers whose only admin-supplied setting is an API key: canonical host,
 * model list fetched live. Everything else needs a hand-typed model (and, for
 * 'custom', a base URL too).
 */
export const KEY_ONLY_PROVIDERS = ['anthropic', 'openai', 'gemini'] as const;

export type KeyOnlyProvider = (typeof KEY_ONLY_PROVIDERS)[number];

export function isKeyOnlyProvider(p: string): p is KeyOnlyProvider {
  return (KEY_ONLY_PROVIDERS as readonly string[]).includes(p);
}
